import { isTransportOrder, type OrderDetails, type OrderSummary } from '#domain/Order.ts';
import type { Stop } from '#domain/Stop.ts';

import type { ApiOrder, ApiServiceOrder, ApiTransportOrder, ApiWriteOrder } from '../model/types.ts';
import { mapMaterials, mapMaterialsToWriteMaterials } from './materialAdapter.ts';
import { mapStop, mapStopToWriteStop } from './stopAdapter.ts';

const mapCommon = (order: ApiOrder) => ({
    id: order.id,
    status: order.status,
    tourId: order.tour_id,
    tourName: order.tour_name,
    truck: order.truck_id ? { id: order.truck_id } : undefined,
    customerOrderId: undefined,
});

const mapOrderSummary = (order: ApiOrder): OrderSummary => {
    if (order.type === 'service') {
        const serviceOrder = order as ApiServiceOrder;
        return {
            ...mapCommon(serviceOrder),
            type: 'service',
            number: serviceOrder.execution_stop.name,
            customerName: '',
            executionStop: mapStop(serviceOrder.execution_stop),
            description: serviceOrder.order,
            note: serviceOrder.note,
        };
    }

    const transportOrder = order as ApiTransportOrder;
    return {
        ...mapCommon(transportOrder),
        type: 'transport',
        number: transportOrder.number,
        customerName: transportOrder.customer_name,
        loadingStop: mapStop(transportOrder.loading_stop),
        unloadingStop: mapStop(transportOrder.unloading_stop),
        loadingComment: transportOrder.comments?.loading,
        unloadingComment: transportOrder.comments?.unloading,
        materials: mapMaterials(transportOrder.materials),
    };
};

export const mapOrderSummaries = (orders: ApiOrder[]): OrderSummary[] => orders.map(mapOrderSummary);

export const mapOrderDetails = (order: ApiOrder): OrderDetails => {
    const summary = mapOrderSummary(order);
    if (isTransportOrder(summary)) {
        const transportOrder = order as ApiTransportOrder;
        return { ...summary, materials: mapMaterials(transportOrder.materials) };
    }
    return summary;
};

const buildWriteComments = (loadingComment?: string, unloadingComment?: string) => {
    if (!loadingComment && !unloadingComment) {
        return undefined;
    }

    return {
        loading: loadingComment,
        unloading: unloadingComment,
    };
};

const ensureStop = (stop: Stop): Stop => ({
    ...stop,
    startTarget: stop.startTarget ?? '',
    endTarget: stop.endTarget ?? '',
});

export const mapOrderToWriteOrder = (order: OrderDetails): ApiWriteOrder => {
    if (!isTransportOrder(order)) {
        return {
            type: 'service',
            order: order.description,
            note: order.note || undefined,
            execution_stop: mapStopToWriteStop(ensureStop(order.executionStop)),
        };
    }

    return {
        type: 'transport',
        number: order.number,
        customer_name: order.customerName,
        comments: buildWriteComments(order.loadingComment, order.unloadingComment),
        loading_stop: mapStopToWriteStop(ensureStop(order.loadingStop)),
        unloading_stop: mapStopToWriteStop(ensureStop(order.unloadingStop)),
        materials: mapMaterialsToWriteMaterials(order.materials ?? []),
    };
};
